import { z } from "zod";
import {
  type MeResponse,
  meResponseSchema,
  type SignupCompleteResponse,
  type SignupVerifyResponse,
  signupCompleteResponseSchema,
  signupRequestResponseSchema,
  signupVerifyResponseSchema,
  type UpdateProfileResponse,
  updateProfileResponseSchema,
} from "@/features/identity/schemas/auth.schema";
import { type MeSummary, meSummarySchema } from "@/features/identity/schemas/me-summary.schema";
import { ApiError, apiCall as call, resolveApiUrl } from "@/shared/lib/api-call";
import { apiPaths } from "@/shared/lib/api-paths";

// Re-exported so existing consumers of `@/features/identity/identity.api`'s
// `ApiError` keep working without a wider rename. New code should import
// from `@/shared/lib/api-call` directly.
export { ApiError };

// =============================================================================
// /auth endpoints
// =============================================================================

export async function requestSignupToken(input: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  planKey?: "trial" | "elevate";
}): Promise<{ sent: true }> {
  return call<{ sent: true }>(
    apiPaths.auth.signupRequest(),
    { method: "POST", body: JSON.stringify(input) },
    signupRequestResponseSchema,
  );
}

export async function verifySignupToken(input: {
  token?: string;
  email?: string;
  otpCode?: string;
}): Promise<SignupVerifyResponse> {
  const body: Record<string, string> = {};
  if (input.token) body.token = input.token;
  if (input.email && input.otpCode) {
    body.email = input.email;
    body.otpCode = input.otpCode;
  }
  return call<SignupVerifyResponse>(
    apiPaths.auth.signupVerify(),
    { method: "POST", body: JSON.stringify(body) },
    signupVerifyResponseSchema,
  );
}

export async function completeSignup(input: {
  token: string;
  planKey?: "trial" | "elevate";
}): Promise<SignupCompleteResponse> {
  return call<SignupCompleteResponse>(
    apiPaths.auth.signupComplete(),
    { method: "POST", body: JSON.stringify(input) },
    signupCompleteResponseSchema,
  );
}

export async function getMe(): Promise<MeResponse> {
  return call<MeResponse>(apiPaths.auth.me(), { method: "GET" }, meResponseSchema);
}

// --- login ----------------------------------------------------------------

/// One of several accounts an email belongs to, offered by the email and the
/// Google sign-in pick-account steps.
export const accountCandidateSchema = z.object({
  accountId: z.string(),
  accountName: z.string(),
  userId: z.string(),
  displayName: z.string(),
});
export type AccountCandidate = z.infer<typeof accountCandidateSchema>;

const loginVerifyResponseSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("signed-in"),
    user: z.unknown(),
    account: z.unknown(),
    defaultWorkspaceId: z.string().nullable(),
  }),
  z.object({
    action: z.literal("pick-account"),
    token: z.string(),
    candidates: z.array(accountCandidateSchema),
  }),
  z.object({
    action: z.literal("redirect-signup"),
    email: z.string().email(),
    token: z.string(),
  }),
]);
export type LoginVerifyResponse = z.infer<typeof loginVerifyResponseSchema>;

/// `returnTo` (a consent page, see isSafeOAuthReturnTo) rides on the emailed
/// link so clicking it lands back on the AI app's Connect.
export async function requestLoginToken(input: {
  email: string;
  returnTo?: string;
}): Promise<{ sent: true }> {
  const body = input.returnTo
    ? { email: input.email, returnTo: input.returnTo }
    : { email: input.email };
  return call<{ sent: true }>(
    apiPaths.auth.loginRequest(),
    { method: "POST", body: JSON.stringify(body) },
    signupRequestResponseSchema,
  );
}

const loginPasswordResponseSchema = z.object({
  action: z.literal("signed-in"),
  user: z.unknown(),
  account: z.unknown(),
  defaultWorkspaceId: z.string().nullable(),
});
export type LoginPasswordResponse = z.infer<typeof loginPasswordResponseSchema>;

export async function loginWithPassword(input: {
  email: string;
  password: string;
}): Promise<LoginPasswordResponse> {
  return call<LoginPasswordResponse>(
    apiPaths.auth.loginPassword(),
    { method: "POST", body: JSON.stringify(input) },
    loginPasswordResponseSchema,
  );
}

// --- password reset / change ----------------------------------------------

export async function requestPasswordReset(input: { email: string }): Promise<{ sent: true }> {
  return call<{ sent: true }>(
    apiPaths.auth.passwordForgot(),
    { method: "POST", body: JSON.stringify(input) },
    z.object({ sent: z.literal(true) }),
  );
}

export async function resetPassword(input: {
  token: string;
  password: string;
}): Promise<{ reset: true }> {
  return call<{ reset: true }>(
    apiPaths.auth.passwordReset(),
    { method: "POST", body: JSON.stringify(input) },
    z.object({ reset: z.literal(true) }),
  );
}

export async function changePassword(input: {
  currentPassword?: string;
  newPassword: string;
}): Promise<{ changed: true }> {
  return call<{ changed: true }>(
    apiPaths.auth.passwordChange(),
    { method: "POST", body: JSON.stringify(input) },
    z.object({ changed: z.literal(true) }),
  );
}

export async function verifyLoginToken(input: {
  token?: string;
  email?: string;
  otpCode?: string;
}): Promise<LoginVerifyResponse> {
  const body: Record<string, string> = {};
  if (input.token) body.token = input.token;
  if (input.email && input.otpCode) {
    body.email = input.email;
    body.otpCode = input.otpCode;
  }
  return call<LoginVerifyResponse>(
    apiPaths.auth.loginVerify(),
    { method: "POST", body: JSON.stringify(body) },
    loginVerifyResponseSchema,
  );
}

/// Finishes an email sign-in whose address is on several accounts. `token`
/// is the one the pick-account verify result returned, not the link's.
export async function pickLoginAccount(input: {
  token: string;
  userId: string;
}): Promise<{ defaultWorkspaceId: string | null }> {
  return call<{ defaultWorkspaceId: string | null }>(
    apiPaths.auth.loginPickAccount(),
    { method: "POST", body: JSON.stringify(input) },
    z.object({ defaultWorkspaceId: z.string().nullable() }),
  );
}

export async function signOut(): Promise<{ signedOut: true }> {
  return call<{ signedOut: true }>(
    apiPaths.auth.signOut(),
    { method: "POST", body: JSON.stringify({}) },
    z.object({ signedOut: z.literal(true) }),
  );
}

// =============================================================================
// /workspaces/:workspaceId/invitations endpoints
// =============================================================================

export async function resendInvitation(workspaceId: string, invitationId: string) {
  return call(
    apiPaths.invites.resend(workspaceId, invitationId),
    { method: "POST", body: JSON.stringify({}) },
    z.object({ resent: z.literal(true) }),
  );
}

export async function resendMemberInvite(workspaceId: string, membershipId: string) {
  return call(
    apiPaths.invites.resendMember(workspaceId),
    { method: "POST", body: JSON.stringify({ membershipId }) },
    z.object({ resent: z.literal(true) }),
  );
}

// -- public accept endpoints --------------------------------------------------

const acceptResponseSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("signed-in"), workspaceId: z.string() }),
  z.object({
    action: z.literal("collect-displayName"),
    acceptanceToken: z.string(),
    email: z.string().email(),
  }),
]);
export type AcceptResponse = z.infer<typeof acceptResponseSchema>;

export async function acceptInvitation(
  input: { token: string } | { email: string; otpCode: string },
): Promise<AcceptResponse> {
  return call<AcceptResponse>(
    apiPaths.invitations.accept(),
    { method: "POST", body: JSON.stringify(input) },
    acceptResponseSchema,
  );
}

export async function completeInvitationAcceptance(input: {
  acceptanceToken: string;
  displayName: string;
}): Promise<{ action: "signed-in"; workspaceId: string }> {
  return call(
    apiPaths.invitations.acceptComplete(),
    { method: "POST", body: JSON.stringify(input) },
    z.object({ action: z.literal("signed-in"), workspaceId: z.string() }),
  );
}

export async function lookupInvitationError(token: string): Promise<{
  inviter: { displayName: string } | null;
}> {
  const params = new URLSearchParams({ token });
  return call(
    `${apiPaths.invitations.lookupError()}?${params.toString()}`,
    { method: "GET" },
    z.object({ inviter: z.object({ displayName: z.string() }).nullable() }),
  );
}

// =============================================================================
// /workspaces lifecycle
// =============================================================================

const workspaceLifecycleSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  name: z.string(),
  slug: z.string(),
  createdAt: z.string(),
  deletedAt: z.string().nullable(),
  memberCount: z.number().int().nonnegative(),
});
export type WorkspaceLifecycle = z.infer<typeof workspaceLifecycleSchema>;

export async function createWorkspace(input: {
  name: string;
  slug?: string;
}): Promise<{ id: string; name: string; slug: string }> {
  return call<{ id: string; name: string; slug: string }>(
    apiPaths.workspaces(),
    { method: "POST", body: JSON.stringify(input) },
    z.object({ id: z.string(), name: z.string(), slug: z.string() }),
  );
}

export async function updateWorkspace(
  workspaceId: string,
  changes: { name?: string; slug?: string; operatingDate?: string | null },
): Promise<{ id: string; name: string; slug: string; operatingDate: string | null }> {
  return call<{ id: string; name: string; slug: string; operatingDate: string | null }>(
    apiPaths.workspace(workspaceId),
    { method: "PATCH", body: JSON.stringify(changes) },
    z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      operatingDate: z.string().nullable().optional(),
    }),
  );
}

export async function softDeleteWorkspace(
  workspaceId: string,
  typedName: string,
): Promise<{ deleted: true }> {
  return call<{ deleted: true }>(
    apiPaths.workspace(workspaceId),
    { method: "DELETE", body: JSON.stringify({ typedName }) },
    z.object({ deleted: z.literal(true) }),
  );
}

export async function restoreWorkspace(workspaceId: string): Promise<{ id: string; slug: string }> {
  return call<{ id: string; slug: string }>(
    apiPaths.workspaceRestore(workspaceId),
    { method: "POST", body: JSON.stringify({}) },
    z.object({ id: z.string(), slug: z.string() }),
  );
}

// =============================================================================
// /workspaces/:workspaceId/members
// =============================================================================

export async function leaveWorkspace(workspaceId: string): Promise<{ left: true }> {
  return call<{ left: true }>(
    apiPaths.members.leave(workspaceId),
    { method: "POST", body: JSON.stringify({}) },
    z.object({ left: z.literal(true) }),
  );
}

// =============================================================================
// /accounts/:accountId + /me
// =============================================================================

const accountPersonSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  email: z.string().email(),
  avatarUrl: z.string().nullable(),
});
const accountAdminSchema = accountPersonSchema.extend({
  accountRole: z.enum(["owner", "admin"]),
});
export type AccountAdmin = z.infer<typeof accountAdminSchema>;
const accountAdminsListSchema = z.object({ items: z.array(accountAdminSchema) });

const accountUserSchema = accountPersonSchema.extend({
  accountRole: z.enum(["owner", "admin", "member"]),
});
export type AccountUser = z.infer<typeof accountUserSchema>;
const accountUsersListSchema = z.object({ items: z.array(accountUserSchema) });

export async function listAccountAdmins(accountId: string): Promise<{ items: AccountAdmin[] }> {
  return call<{ items: AccountAdmin[] }>(
    apiPaths.account.admins(accountId),
    { method: "GET" },
    accountAdminsListSchema,
  );
}

export async function listAccountUsers(accountId: string): Promise<{ items: AccountUser[] }> {
  return call<{ items: AccountUser[] }>(
    apiPaths.account.users(accountId),
    { method: "GET" },
    accountUsersListSchema,
  );
}

export async function grantAccountAdmin(
  accountId: string,
  userId: string,
): Promise<{ granted: boolean }> {
  return call<{ granted: boolean }>(
    apiPaths.account.admins(accountId),
    { method: "POST", body: JSON.stringify({ userId }) },
    z.object({ granted: z.boolean() }),
  );
}

export async function revokeAccountAdmin(
  accountId: string,
  userId: string,
): Promise<{ revoked: boolean }> {
  return call<{ revoked: boolean }>(
    apiPaths.account.admin(accountId, userId),
    { method: "DELETE" },
    z.object({ revoked: z.boolean() }),
  );
}

export async function listAccountWorkspaces(
  accountId: string,
  includeDeleted?: boolean,
): Promise<{ items: WorkspaceLifecycle[] }> {
  const qs = includeDeleted ? "?includeDeleted=true" : "";
  return call<{ items: WorkspaceLifecycle[] }>(
    `${apiPaths.account.workspaces(accountId)}${qs}`,
    { method: "GET" },
    z.object({ items: z.array(workspaceLifecycleSchema) }),
  );
}

export async function updateAccountName(
  accountId: string,
  name: string,
): Promise<{ id: string; name: string }> {
  return call<{ id: string; name: string }>(
    apiPaths.account.detail(accountId),
    { method: "PATCH", body: JSON.stringify({ name }) },
    z.object({ id: z.string(), name: z.string() }),
  );
}

/// The profile is read-only by design: avatarUrl is the only self-serve field
/// the shore accepts (anything else 400s via forbidNonWhitelisted).
export async function updateProfile(input: {
  avatarUrl?: string | null;
}): Promise<UpdateProfileResponse> {
  return call<UpdateProfileResponse>(
    apiPaths.me.detail(),
    { method: "PATCH", body: JSON.stringify(input) },
    updateProfileResponseSchema,
  );
}

/// The caller's own aggregated profile within a workspace (Summary tab).
export async function getMeSummary(workspaceId: string): Promise<MeSummary> {
  return call<MeSummary>(apiPaths.me.summary(workspaceId), { method: "GET" }, meSummarySchema);
}

export async function uploadAvatar(file: File): Promise<UpdateProfileResponse> {
  const body = new FormData();
  body.append("file", file);
  return call<UpdateProfileResponse>(
    apiPaths.me.avatar(),
    { method: "POST", body },
    updateProfileResponseSchema,
  );
}

// =============================================================================
// /auth/google
// =============================================================================

/** Returns the absolute URL the browser should navigate to. */
export function googleStartUrl(returnTo?: string): string {
  const query = returnTo ? `?${new URLSearchParams({ returnTo }).toString()}` : "";
  return resolveApiUrl(`${apiPaths.auth.googleStart()}${query}`);
}

const googlePickAccountResponseSchema = z.object({
  workspaceId: z.string().nullable(),
  /// The consent return-to carried through the signed handoff, when the
  /// sign-in started from an AI app's Connect.
  returnTo: z.string().nullable().optional(),
});
export type GooglePickAccountResponse = z.infer<typeof googlePickAccountResponseSchema>;

export async function googlePickAccount(input: {
  handoff: string;
  userId: string;
}): Promise<GooglePickAccountResponse> {
  return call<GooglePickAccountResponse>(
    apiPaths.auth.googlePickAccount(),
    { method: "POST", body: JSON.stringify(input) },
    googlePickAccountResponseSchema,
  );
}
